import React, { useState, useEffect } from 'react';
import '../styles/CookieConsentBanner.css';

export type CookiePreferences = {
  essential: boolean; // always true, non-configurable
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
};

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  functional: false,
  marketing: false,
};

const STORAGE_KEY = 'cookie_preferences';
const BANNER_DISMISSED_KEY = 'cookie_banner_dismissed';

export const CookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem(STORAGE_KEY);
    const bannerDismissed = localStorage.getItem(BANNER_DISMISSED_KEY);

    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (e) {
        console.error('Failed to parse cookie preferences:', e);
      }
    }

    // Show banner only if user hasn't made a choice yet
    if (!bannerDismissed && !savedPreferences) {
      setShowBanner(true);
    }
  }, []);

  // Apply preferences to analytics/tracking
  useEffect(() => {
    applyPreferences(preferences);
  }, [preferences]);

  const applyPreferences = (prefs: CookiePreferences) => {
    // Enable/disable Google Analytics based on analytics preference
    if (prefs.analytics) {
      loadGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }

    // Apply functional cookies (e.g., localStorage for workflow state)
    if (prefs.functional) {
      // Functional cookies are stored in localStorage - already available
      document.documentElement.setAttribute('data-functional-cookies', 'true');
    } else {
      document.documentElement.removeAttribute('data-functional-cookies');
    }

    // Apply marketing cookies (e.g., third-party ad networks)
    if (prefs.marketing) {
      loadMarketingCookies();
    } else {
      disableMarketingCookies();
    }

    // Store the consent in cookie (must be done regardless of preference)
    setConsentCookie(prefs);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allAccepted));
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    setPreferences(DEFAULT_PREFERENCES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_PREFERENCES));
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleTogglePreference = (
    key: keyof Omit<CookiePreferences, 'essential'>
  ) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      {showBanner && !showPreferences && (
        <div className="cookie-banner" role="region" aria-label="Cookie consent">
          <div className="cookie-banner-content">
            <div className="cookie-banner-text">
              <h3>Cookie Preferences</h3>
              <p>
                We use cookies to enhance your experience, analyze site usage, and assist with
                marketing efforts. By continuing, you consent to our use of cookies. You can
                customize your preferences or learn more in our{' '}
                <a href="/cookies.html" target="_blank" rel="noopener noreferrer">
                  Cookie Policy
                </a>
                .
              </p>
            </div>

            <div className="cookie-banner-actions">
              <button
                className="cookie-banner-btn cookie-banner-btn-secondary"
                onClick={() => setShowPreferences(true)}
              >
                Customize
              </button>
              <button
                className="cookie-banner-btn cookie-banner-btn-secondary"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button
                className="cookie-banner-btn cookie-banner-btn-primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreferences && (
        <div className="cookie-preferences-modal">
          <div className="cookie-preferences-overlay" onClick={() => setShowPreferences(false)} />

          <div className="cookie-preferences-content">
            <div className="cookie-preferences-header">
              <h2>Cookie Preferences</h2>
              <button
                className="cookie-preferences-close"
                onClick={() => setShowPreferences(false)}
                aria-label="Close preferences"
              >
                ✕
              </button>
            </div>

            <div className="cookie-preferences-body">
              <p>
                Manage your cookie preferences below. Visit our{' '}
                <a href="/cookies.html" target="_blank" rel="noopener noreferrer">
                  Cookie Policy
                </a>{' '}
                for more information.
              </p>

              <div className="cookie-preference-item">
                <div className="cookie-preference-header">
                  <label className="cookie-preference-toggle">
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      aria-label="Essential cookies"
                    />
                    <span className="toggle-label">Essential Cookies</span>
                  </label>
                  <span className="cookie-preference-required">Required</span>
                </div>
                <p className="cookie-preference-description">
                  Essential cookies are necessary for the website to function properly. They
                  include session management, security, and authentication. These cannot be disabled.
                </p>
              </div>

              <div className="cookie-preference-item">
                <label className="cookie-preference-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handleTogglePreference('analytics')}
                    aria-label="Analytics cookies"
                  />
                  <span className="toggle-label">Analytics Cookies</span>
                </label>
                <p className="cookie-preference-description">
                  Analytics cookies help us understand how you use the website. We use Google
                  Analytics to track page views, user interactions, and feature usage to improve
                  our service.
                </p>
              </div>

              <div className="cookie-preference-item">
                <label className="cookie-preference-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={() => handleTogglePreference('functional')}
                    aria-label="Functional cookies"
                  />
                  <span className="toggle-label">Functional Cookies</span>
                </label>
                <p className="cookie-preference-description">
                  Functional cookies enhance your experience by remembering your preferences,
                  workflow state, and other settings across sessions.
                </p>
              </div>

              <div className="cookie-preference-item">
                <label className="cookie-preference-toggle">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handleTogglePreference('marketing')}
                    aria-label="Marketing cookies"
                  />
                  <span className="toggle-label">Marketing Cookies</span>
                </label>
                <p className="cookie-preference-description">
                  Marketing cookies track conversions and enable remarketing campaigns. They help
                  us deliver relevant content and measure advertising effectiveness.
                </p>
              </div>
            </div>

            <div className="cookie-preferences-footer">
              <button
                className="cookie-banner-btn cookie-banner-btn-secondary"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button
                className="cookie-banner-btn cookie-banner-btn-primary"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper functions to manage tracking scripts

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

const loadGoogleAnalytics = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[Analytics] VITE_GA_MEASUREMENT_ID is not set — skipping GA load');
    return;
  }
  if (window.gtag) return; // Already loaded

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window.gtag as any) = function () {
    (window as any).dataLayer.push(arguments);
  };
  (window.gtag as any)('js', new Date());
  (window.gtag as any)('config', GA_MEASUREMENT_ID);
};

const disableGoogleAnalytics = () => {
  if (!GA_MEASUREMENT_ID) return;
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
};

const loadMarketingCookies = () => {
  // Load third-party marketing pixels here (Facebook Pixel, etc.)
  // Example:
  // const script = document.createElement('script');
  // script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  // document.head.appendChild(script);
};

const disableMarketingCookies = () => {
  // Disable marketing cookies
  // This can be done by preventing third-party scripts from loading
};

const setConsentCookie = (preferences: CookiePreferences) => {
  const consentValue = JSON.stringify(preferences);
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);

  document.cookie = `cookie_consent=${encodeURIComponent(consentValue)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
};

// Type augmentation for window object
declare global {
  interface Window {
    gtag?: any;
    dataLayer?: any[];
  }
}

export default CookieConsentBanner;
